import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Plus, Trash2, Paperclip, CheckSquare, X, FileText, Download, Eye, ClipboardList, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (v) => {
  if (!v && v !== 0) return '';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
};
const fmtDate = (d) => {
  if (!d) return '';
  try { const [y, m, day] = d.split('-'); const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${parseInt(day)} ${months[parseInt(m)-1]} ${y}`; } catch { return d; }
};

const STATUS_COLORS = {
  'Approved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Pending Approval': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  'Rejected': 'bg-red-50 text-red-700 border-red-200',
};
const StatusBadge = ({ status }) => (
  <span className={`inline-block px-2 py-px rounded border text-[10px] font-semibold tracking-wide uppercase whitespace-nowrap ${STATUS_COLORS[status] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>
    {status === 'Pending Approval' ? 'PENDING' : (status || '').toUpperCase()}
  </span>
);

export default function BudgetRequestsPage() {
  const { getAuthHeader, employee } = useAuth();
  const role = employee?.role || 'Employee';
  const canCreate = ['Admin Team', 'Admin Head', 'Super Admin'].includes(role);
  const canApprove = ['Super Admin', 'Management'].includes(role);
  const hasAccess = ['Admin Team', 'Admin Head', 'Super Admin', 'Management'].includes(role);

  const [activeTab, setActiveTab] = useSessionState('budget-requests:activeTab', canCreate ? 'my-requests' : 'approvals');
  const [myRequests, setMyRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOptions, setFormOptions] = useState({ categories: {}, locations: [], entities: [] });
  const [createOpen, setCreateOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);

  // Approval state
  const [selectedPending, setSelectedPending] = useState([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [vendorSelectDialog, setVendorSelectDialog] = useState(null); // holds the request being approved
  const [selectedVendorIdx, setSelectedVendorIdx] = useState(null);
  const [approvalRemarks, setApprovalRemarks] = useState('');

  // Form state
  const emptyVendor = { vendor_name: '', quoted_amount: '', remarks: '', attachment: null };
  const [form, setForm] = useState({ location: '', category: '', description: '', required_by_date: '', entity: '', vendor_quotations: [{ ...emptyVendor }] });
  const [saving, setSaving] = useState(false);

  const headers = useCallback(() => ({ ...getAuthHeader(), 'Content-Type': 'application/json' }), [getAuthHeader]);

  const fetchFormOptions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/expenses/form-options`, { headers: getAuthHeader() });
      if (res.ok) setFormOptions(await res.json());
    } catch { /* */ }
  }, [getAuthHeader]);

  const fetchMyRequests = useCallback(async () => {
    try {
      const res = await fetch(`${API}/budget-requests?my_requests=true`, { headers: getAuthHeader() });
      if (res.ok) setMyRequests(await res.json());
    } catch { /* */ }
  }, [getAuthHeader]);

  const fetchAllRequests = useCallback(async () => {
    try {
      const res = await fetch(`${API}/budget-requests`, { headers: getAuthHeader() });
      if (res.ok) setAllRequests(await res.json());
    } catch { /* */ }
  }, [getAuthHeader]);

  const fetchPending = useCallback(async () => {
    if (!canApprove) return;
    try {
      const res = await fetch(`${API}/budget-requests/pending-approvals`, { headers: getAuthHeader() });
      if (res.ok) { setPendingRequests(await res.json()); setSelectedPending([]); }
    } catch { /* */ }
  }, [getAuthHeader, canApprove]);

  useEffect(() => {
    if (!hasAccess) { setLoading(false); return; }
    Promise.all([fetchFormOptions(), fetchMyRequests(), fetchAllRequests(), fetchPending()]).finally(() => setLoading(false));
  }, [fetchFormOptions, fetchMyRequests, fetchAllRequests, fetchPending, hasAccess]);

  const categoryNames = useMemo(() => Object.keys(formOptions.categories || {}), [formOptions]);

  const addVendor = () => setForm(p => ({ ...p, vendor_quotations: [...p.vendor_quotations, { ...emptyVendor }] }));
  const removeVendor = (idx) => setForm(p => ({ ...p, vendor_quotations: p.vendor_quotations.filter((_, i) => i !== idx) }));
  const updateVendor = (idx, field, value) => setForm(p => {
    const vqs = [...p.vendor_quotations];
    vqs[idx] = { ...vqs[idx], [field]: value };
    return { ...p, vendor_quotations: vqs };
  });

  const handleVendorFileUpload = async (idx, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/upload`, { method: 'POST', headers: getAuthHeader(), body: fd });
      if (res.ok) { const d = await res.json(); updateVendor(idx, 'attachment', d.url || d.file_url); toast.success('File uploaded'); }
      else toast.error('Upload failed');
    } catch { toast.error('Upload failed'); }
  };

  const handleCreate = async () => {
    const loc = formOptions.location_locked ? formOptions.default_location : form.location;
    if (!loc || !form.category || !form.description.trim()) { toast.error('Location, Category, and Description are required'); return; }
    const validVendors = form.vendor_quotations.filter(v => v.vendor_name && v.quoted_amount);
    if (validVendors.length === 0) { toast.error('At least one vendor with name and amount is required'); return; }
    setSaving(true);
    try {
      const body = { ...form, location: loc, vendor_quotations: validVendors.map(v => ({ ...v, quoted_amount: parseFloat(v.quoted_amount) || 0 })) };
      const res = await fetch(`${API}/budget-requests`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
      if (res.ok) { toast.success('Budget request submitted'); setCreateOpen(false); setForm({ location: '', category: '', description: '', required_by_date: '', entity: '', vendor_quotations: [{ ...emptyVendor }] }); fetchMyRequests(); fetchAllRequests(); fetchPending(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleApprovalAction = async (action, overrideIds, vendorIdx) => {
    const ids = overrideIds || selectedPending;
    if (ids.length === 0) { toast.error('Select requests'); return; }
    if (action === 'reject' && !rejectRemarks.trim()) { setRejectDialog(true); return; }
    
    // For approve with single request, open vendor selection dialog
    if (action === 'approve' && ids.length === 1 && vendorIdx === undefined) {
      const req = pendingRequests.find(r => r.request_id === ids[0]);
      if (req && req.vendor_quotations?.length > 0) {
        setVendorSelectDialog(req);
        setSelectedVendorIdx(req.selected_vendor_index ?? null);
        setApprovalRemarks('');
        return;
      }
    }
    
    setApprovalLoading(true);
    try {
      const res = await fetch(`${API}/budget-requests/approval-action`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ request_ids: ids, action, remarks: action === 'reject' ? rejectRemarks : approvalRemarks, selected_vendor_index: vendorIdx ?? null }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message); setSelectedPending([]); setRejectRemarks(''); setRejectDialog(false); setVendorSelectDialog(null); setSelectedVendorIdx(null); setApprovalRemarks(''); fetchPending(); fetchMyRequests(); fetchAllRequests(); }
      else toast.error(data.detail || 'Failed');
    } catch { toast.error('Failed'); }
    finally { setApprovalLoading(false); }
  };

  const downloadAttachment = async (url, name) => {
    try {
      const res = await fetch(`${BACKEND}${url}`, { headers: getAuthHeader() });
      if (res.ok) { const blob = await res.blob(); const u = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = u; a.download = name || 'quotation'; document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(u); document.body.removeChild(a); }
    } catch { toast.error('Download failed'); }
  };

  const toggleSelect = (rid) => setSelectedPending(p => p.includes(rid) ? p.filter(id => id !== rid) : [...p, rid]);
  const selectAll = () => setSelectedPending(p => p.length === pendingRequests.length ? [] : pendingRequests.map(r => r.request_id));

  if (!hasAccess) return <Layout><div className="flex items-center justify-center min-h-[60vh]"><p className="text-muted-foreground">Access denied.</p></div></Layout>;

  const RequestTable = ({ data, showApprover = false, showApprovedAmt = false }) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow className="bg-gray-100 dark:bg-zinc-800 border-b border-gray-200">
          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">ID</TableHead>
          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Date</TableHead>
          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Location</TableHead>
          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Category</TableHead>
          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Description</TableHead>
          {showApprovedAmt && <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-right">Approved Amt</TableHead>}
          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Vendors</TableHead>
          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</TableHead>
          {showApprover && <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Created By</TableHead>}
          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Required By</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {data.map((r, idx) => {
            return (
              <TableRow key={r.request_id} className={`h-10 hover:bg-accent/40 transition-colors cursor-pointer ${idx % 2 === 0 ? 'bg-white dark:bg-zinc-900' : 'bg-gray-50/60 dark:bg-zinc-800/20'}`} onClick={() => setDetailDialog(r)}>
                <TableCell className="py-2 text-[11px] font-mono text-blue-600">{r.request_id}</TableCell>
                <TableCell className="py-2 text-[12px] whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                <TableCell className="py-2 text-[12px]">{r.location}</TableCell>
                <TableCell className="py-2 text-[12px] max-w-[130px] truncate">{r.category}</TableCell>
                <TableCell className="py-2 text-[12px] max-w-[180px] truncate text-muted-foreground" title={r.description}>{r.description}</TableCell>
                {showApprovedAmt && <TableCell className="py-2 text-[12px] text-right font-semibold tabular-nums">{r.approved_budget_amount ? fmtCurrency(r.approved_budget_amount) : <span className="text-muted-foreground font-normal">-</span>}</TableCell>}
                <TableCell className="py-2 text-[11px]">{(r.vendor_quotations || []).length}</TableCell>
                <TableCell className="py-2"><StatusBadge status={r.status} /></TableCell>
                {showApprover && <TableCell className="py-2 text-[11px] text-muted-foreground">{(r.created_by || '').split(' ').slice(0, 2).join(' ')}</TableCell>}
                <TableCell className="py-2 text-[12px] whitespace-nowrap">{fmtDate(r.required_by_date)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-5" data-testid="budget-requests-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Budget Requests</h1>
            <p className="text-muted-foreground text-sm mt-0.5">Vendor quotation & budget approval</p>
          </div>
          {canCreate && <Button size="sm" className="h-8 text-xs" onClick={() => setCreateOpen(true)} data-testid="create-budget-btn"><Plus className="w-3.5 h-3.5 mr-1" />New Request</Button>}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-9">
            {canCreate && <TabsTrigger value="my-requests" className="text-xs" data-testid="tab-my-requests"><ClipboardList className="w-3.5 h-3.5 mr-1" />My Requests</TabsTrigger>}
            {canApprove && <TabsTrigger value="approvals" className="text-xs" data-testid="tab-budget-approvals"><CheckSquare className="w-3.5 h-3.5 mr-1" />Approvals{pendingRequests.length > 0 && <Badge variant="destructive" className="ml-1.5 px-1.5 py-0 text-[10px] h-4 rounded-full">{pendingRequests.length}</Badge>}</TabsTrigger>}
            {canApprove && <TabsTrigger value="all" className="text-xs" data-testid="tab-all-requests"><BarChart3 className="w-3.5 h-3.5 mr-1" />All Requests</TabsTrigger>}
          </TabsList>

          {canCreate && (
            <TabsContent value="my-requests" className="mt-4">
              <Card className="bg-gray-50/80 dark:bg-zinc-900/50 border-gray-200">
                <CardHeader className="pb-3 pt-4 px-4"><CardTitle className="text-base">My Budget Requests</CardTitle><CardDescription>{myRequests.length} request(s)</CardDescription></CardHeader>
                <CardContent className="p-0">
                  {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div> :
                    myRequests.length === 0 ? <div className="text-center py-12 text-muted-foreground"><p className="text-sm">No budget requests yet</p></div> :
                    <RequestTable data={myRequests} />}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {canApprove && (
            <TabsContent value="approvals" className="mt-4">
              <Card className="bg-gray-50/80 dark:bg-zinc-900/50 border-gray-200">
                <CardHeader className="pb-3 pt-4 px-4">
                  <div className="flex items-center justify-between">
                    <div><CardTitle className="text-base">Pending Approvals</CardTitle><CardDescription>{pendingRequests.length} request(s) awaiting approval</CardDescription></div>
                    {selectedPending.length > 0 && (
                      <div className="flex gap-2 items-center">
                        <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApprovalAction('approve')} disabled={approvalLoading} data-testid="bulk-approve-budget"><CheckSquare className="w-3.5 h-3.5 mr-1" />Approve ({selectedPending.length})</Button>
                        <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={() => setRejectDialog(true)} disabled={approvalLoading} data-testid="bulk-reject-budget"><X className="w-3.5 h-3.5 mr-1" />Reject ({selectedPending.length})</Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {pendingRequests.length === 0 ? <div className="text-center py-12 text-muted-foreground"><CheckSquare className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No pending approvals</p></div> : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader><TableRow className="bg-gray-100 dark:bg-zinc-800 border-b border-gray-200">
                          <TableHead className="h-9 w-10"><Checkbox checked={selectedPending.length === pendingRequests.length && pendingRequests.length > 0} onCheckedChange={selectAll} className="h-3.5 w-3.5" /></TableHead>
                          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">ID</TableHead>
                          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Date</TableHead>
                          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Location</TableHead>
                          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Category</TableHead>
                          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Description</TableHead>
                          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-right">Total Quoted</TableHead>
                          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Vendors</TableHead>
                          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Created By</TableHead>
                          <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Required By</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {pendingRequests.map((r, idx) => {
                            const total = (r.vendor_quotations || []).reduce((s, v) => s + (v.quoted_amount || 0), 0);
                            return (
                              <TableRow key={r.request_id} className={`h-10 hover:bg-accent/40 ${idx % 2 === 0 ? 'bg-white dark:bg-zinc-900' : 'bg-gray-50/60'}`}>
                                <TableCell className="py-2"><Checkbox checked={selectedPending.includes(r.request_id)} onCheckedChange={() => toggleSelect(r.request_id)} className="h-3.5 w-3.5" /></TableCell>
                                <TableCell className="py-2 text-[11px] font-mono text-blue-600 cursor-pointer hover:underline" onClick={() => setDetailDialog(r)}>{r.request_id}</TableCell>
                                <TableCell className="py-2 text-[12px] whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                                <TableCell className="py-2 text-[12px]">{r.location}</TableCell>
                                <TableCell className="py-2 text-[12px] max-w-[130px] truncate">{r.category}</TableCell>
                                <TableCell className="py-2 text-[12px] max-w-[160px] truncate text-muted-foreground" title={r.description}>{r.description}</TableCell>
                                <TableCell className="py-2 text-[12px] text-right font-semibold tabular-nums">{fmtCurrency(total)}</TableCell>
                                <TableCell className="py-2 text-[11px]">{(r.vendor_quotations || []).length}</TableCell>
                                <TableCell className="py-2 text-[11px] text-muted-foreground">{(r.created_by || '').split(' ').slice(0, 2).join(' ')}</TableCell>
                                <TableCell className="py-2 text-[12px] whitespace-nowrap">{fmtDate(r.required_by_date)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {canApprove && (
          <TabsContent value="all" className="mt-4">
            <Card className="bg-gray-50/80 dark:bg-zinc-900/50 border-gray-200">
              <CardHeader className="pb-3 pt-4 px-4"><CardTitle className="text-base">All Budget Requests</CardTitle><CardDescription>{allRequests.length} request(s)</CardDescription></CardHeader>
              <CardContent className="p-0">
                {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div> :
                  allRequests.length === 0 ? <div className="text-center py-12 text-muted-foreground"><p className="text-sm">No requests</p></div> :
                  <RequestTable data={allRequests} showApprover showApprovedAmt />}
              </CardContent>
            </Card>
          </TabsContent>
          )}
        </Tabs>

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Budget Approval Request</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Location *</Label>
                  {formOptions.location_locked ? <Input value={formOptions.default_location} readOnly className="h-9 bg-muted cursor-not-allowed" /> : (
                    <Select value={form.location} onValueChange={v => setForm(p => ({ ...p, location: v }))}>
                      <SelectTrigger className="h-9" data-testid="bar-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                      <SelectContent>{(formOptions.locations || []).map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-1"><Label className="text-xs">Category *</Label>
                  <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="h-9" data-testid="bar-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{categoryNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Entity</Label>
                  <Select value={form.entity} onValueChange={v => setForm(p => ({ ...p, entity: v }))}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Select entity" /></SelectTrigger>
                    <SelectContent>{(formOptions.entities || ['UOSPL','UAFPL','UAPL']).map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Required By Date</Label><Input type="date" value={form.required_by_date} onChange={e => setForm(p => ({ ...p, required_by_date: e.target.value }))} className="h-9" data-testid="bar-required-date" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Description *</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe the budget requirement..." rows={3} className="text-sm" data-testid="bar-description" /></div>

              <Separator />
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Vendor Quotations</h3>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addVendor} data-testid="add-vendor-btn"><Plus className="w-3 h-3 mr-1" />Add Vendor</Button>
              </div>
              {form.vendor_quotations.map((vq, idx) => (
                <Card key={idx} className="p-3 bg-white dark:bg-zinc-900">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1"><Label className="text-[10px]">Vendor Name *</Label><Input value={vq.vendor_name} onChange={e => updateVendor(idx, 'vendor_name', e.target.value)} placeholder="Vendor" className="h-8 text-xs" data-testid={`vendor-name-${idx}`} /></div>
                        <div className="space-y-1"><Label className="text-[10px]">Quoted Amount *</Label><Input type="number" value={vq.quoted_amount} onChange={e => updateVendor(idx, 'quoted_amount', e.target.value)} placeholder="0" className="h-8 text-xs" data-testid={`vendor-amount-${idx}`} /></div>
                        <div className="space-y-1"><Label className="text-[10px]">Remarks</Label><Input value={vq.remarks} onChange={e => updateVendor(idx, 'remarks', e.target.value)} placeholder="Optional" className="h-8 text-xs" /></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input type="file" className="h-8 text-xs flex-1" onChange={e => { if (e.target.files?.[0]) handleVendorFileUpload(idx, e.target.files[0]); }} data-testid={`vendor-file-${idx}`} />
                        {vq.attachment && <span className="text-[10px] text-emerald-600 flex items-center gap-1"><Paperclip className="w-3 h-3" />Uploaded</span>}
                      </div>
                    </div>
                    {form.vendor_quotations.length > 1 && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive mt-4" onClick={() => removeVendor(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                </Card>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={saving} data-testid="submit-budget-btn">{saving ? 'Submitting...' : 'Submit Request'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {detailDialog && (
              <>
                <DialogHeader><DialogTitle className="flex items-center gap-2">{detailDialog.request_id} <StatusBadge status={detailDialog.status} /></DialogTitle>
                  <DialogDescription>Created by {detailDialog.created_by} on {fmtDate(detailDialog.date)}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-muted-foreground text-xs">Location</span><p>{detailDialog.location}</p></div>
                    <div><span className="text-muted-foreground text-xs">Category</span><p>{detailDialog.category}</p></div>
                    <div><span className="text-muted-foreground text-xs">Entity</span><p>{detailDialog.entity || '-'}</p></div>
                    <div><span className="text-muted-foreground text-xs">Required By</span><p>{fmtDate(detailDialog.required_by_date) || '-'}</p></div>
                    {detailDialog.approved_budget_amount != null && detailDialog.approved_budget_amount > 0 && (
                      <>
                        <div><span className="text-muted-foreground text-xs">Approved Vendor</span><p className="font-semibold text-emerald-700">{detailDialog.selected_vendor_name || '-'}</p></div>
                        <div><span className="text-muted-foreground text-xs">Approved Amount</span><p className="font-semibold text-emerald-700">{fmtCurrency(detailDialog.approved_budget_amount)}</p></div>
                      </>
                    )}
                  </div>
                  <div><span className="text-muted-foreground text-xs">Description</span><p className="text-sm mt-0.5">{detailDialog.description}</p></div>

                  <Separator />
                  <h3 className="text-sm font-semibold">Vendor Quotations ({(detailDialog.vendor_quotations || []).length})</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow className="bg-gray-100 dark:bg-zinc-800">
                        <TableHead className="h-8 text-[11px] font-semibold">Vendor</TableHead>
                        <TableHead className="h-8 text-[11px] font-semibold text-right">Amount</TableHead>
                        <TableHead className="h-8 text-[11px] font-semibold">Remarks</TableHead>
                        <TableHead className="h-8 text-[11px] font-semibold w-20">Attachment</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {(detailDialog.vendor_quotations || []).map((v, i) => (
                          <TableRow key={i} className="h-9">
                            <TableCell className="py-1 text-sm font-medium">{v.vendor_name}</TableCell>
                            <TableCell className="py-1 text-sm text-right font-semibold tabular-nums">{fmtCurrency(v.quoted_amount)}</TableCell>
                            <TableCell className="py-1 text-xs text-muted-foreground">{v.remarks || '-'}</TableCell>
                            <TableCell className="py-1">
                              {v.attachment ? <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => downloadAttachment(v.attachment, `${detailDialog.request_id}_vendor_${i}`)}><Download className="w-3 h-3 mr-1" />Download</Button> : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {detailDialog.approval_history?.length > 0 && (
                    <>
                      <Separator />
                      <h3 className="text-sm font-semibold">Approval History</h3>
                      {detailDialog.approval_history.map((h, i) => (
                        <div key={i} className="text-xs text-muted-foreground border-l-2 border-gray-200 pl-3 py-1">
                          <span className="font-medium text-foreground">{h.by}</span> ({h.role}) {h.action}d on {fmtDate(h.at?.split('T')[0])}
                          {h.remarks && <p className="mt-0.5 italic">"{h.remarks}"</p>}
                        </div>
                      ))}
                    </>
                  )}
                </div>
                {canApprove && detailDialog.status === 'Pending Approval' && (
                  <DialogFooter className="pt-3 border-t">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        setSelectedPending([detailDialog.request_id]);
                        setDetailDialog(null);
                        setRejectDialog(true);
                      }}
                      data-testid="detail-reject-btn"
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        const req = detailDialog;
                        setDetailDialog(null);
                        if (req.vendor_quotations?.length > 0) {
                          setVendorSelectDialog(req);
                          setSelectedVendorIdx(req.selected_vendor_index ?? null);
                          setApprovalRemarks('');
                        } else {
                          handleApprovalAction('approve', [req.request_id], null);
                        }
                      }}
                      data-testid="detail-approve-btn"
                    >
                      <CheckSquare className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                  </DialogFooter>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Vendor Selection Dialog (on Approve) */}
        <Dialog open={!!vendorSelectDialog} onOpenChange={() => setVendorSelectDialog(null)}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Select Vendor to Approve</DialogTitle>
              <DialogDescription>Choose a vendor for {vendorSelectDialog?.request_id}. The approved amount will auto-set from the selected vendor's quotation.</DialogDescription>
            </DialogHeader>
            {vendorSelectDialog && (
              <div className="space-y-3 py-2">
                {(vendorSelectDialog.vendor_quotations || []).map((v, i) => (
                  <div key={i} onClick={() => setSelectedVendorIdx(i)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedVendorIdx === i ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-gray-200 hover:border-gray-300 dark:border-zinc-700'}`}
                    data-testid={`vendor-option-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedVendorIdx === i ? 'border-emerald-500' : 'border-gray-300'}`}>
                          {selectedVendorIdx === i && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{v.vendor_name}</p>
                          {v.remarks && <p className="text-xs text-muted-foreground">{v.remarks}</p>}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold tabular-nums">{fmtCurrency(v.quoted_amount)}</p>
                        {v.attachment && <Button variant="ghost" size="sm" className="h-5 px-1 text-[10px]" onClick={(e) => { e.stopPropagation(); downloadAttachment(v.attachment, `${vendorSelectDialog.request_id}_v${i}`); }}><Download className="w-3 h-3 mr-0.5" />Quote</Button>}
                      </div>
                    </div>
                  </div>
                ))}
                {selectedVendorIdx !== null && (
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 border border-emerald-200">
                    <p className="text-xs text-emerald-700 font-medium">Approved Vendor: {vendorSelectDialog.vendor_quotations[selectedVendorIdx]?.vendor_name}</p>
                    <p className="text-lg font-bold text-emerald-700">{fmtCurrency(vendorSelectDialog.vendor_quotations[selectedVendorIdx]?.quoted_amount)}</p>
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs">Remarks (optional)</Label>
                  <Textarea value={approvalRemarks} onChange={e => setApprovalRemarks(e.target.value)} placeholder="Optional approval remarks" rows={2} className="text-sm" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setVendorSelectDialog(null)}>Cancel</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={selectedVendorIdx === null || approvalLoading}
                onClick={() => handleApprovalAction('approve', [vendorSelectDialog.request_id], selectedVendorIdx)}
                data-testid="confirm-vendor-approve">
                {approvalLoading ? 'Approving...' : 'Approve with Selected Vendor'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Reject Request(s)</DialogTitle></DialogHeader>
            <div className="space-y-3 py-1">
              <p className="text-xs text-muted-foreground">Rejecting {selectedPending.length} request(s). Remarks required.</p>
              <div className="space-y-1"><Label className="text-xs">Remarks *</Label><Textarea value={rejectRemarks} onChange={e => setRejectRemarks(e.target.value)} placeholder="Reason for rejection" rows={3} className="text-sm" data-testid="budget-reject-remarks" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setRejectDialog(false)}>Cancel</Button>
              <Button variant="destructive" size="sm" disabled={!rejectRemarks.trim() || approvalLoading} onClick={() => handleApprovalAction('reject')} data-testid="confirm-budget-reject">Reject</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
